/**
 * useNativeRecorder — Capacitor native recording via custom plugin.
 * Used on Android/iOS. Web uses useWebRecorder instead.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import StoryForgeRecorder, { type StopRecordingResult } from "@/plugins/storyforge-recorder";
import type { RecorderStatus } from "./useWebRecorder";

export function useNativeRecorder(projectId: string) {
  const platform = Capacitor.getPlatform();
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasOrphans, setHasOrphans] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef("");

  // Check for orphaned recordings on mount
  useEffect(() => {
    StoryForgeRecorder.getOrphanedRecordings()
      .then((result) => setHasOrphans(result.recordings.length > 0))
      .catch(() => {});
  }, [projectId]);

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const start = useCallback(async () => {
    try {
      setError(null);
      setStatus("requesting");

      const perm = await StoryForgeRecorder.checkPermissions();
      if (perm.microphone !== "granted") {
        const result = await StoryForgeRecorder.requestPermissions();
        if (result.microphone !== "granted") {
          setError("Microphone permission denied");
          setStatus("error");
          return;
        }
      }

      const sessionId = `${projectId}-${Date.now()}`;
      sessionIdRef.current = sessionId;

      await StoryForgeRecorder.startRecording({
        projectId,
        sessionId,
        sampleRate: 48000,
        bitRate: 64000,
      });

      setElapsedSeconds(0);
      setStatus("recording");
      startTimer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Recording failed");
      setStatus("error");
    }
  }, [projectId, startTimer]);

  const pause = useCallback(async () => {
    try {
      await StoryForgeRecorder.pauseRecording();
      setStatus("paused");
      stopTimer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Pause failed");
    }
  }, [stopTimer]);

  const resume = useCallback(async () => {
    try {
      await StoryForgeRecorder.resumeRecording();
      setStatus("recording");
      startTimer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Resume failed");
    }
  }, [startTimer]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    try {
      setStatus("saving");
      stopTimer();

      const result: StopRecordingResult = await StoryForgeRecorder.stopRecording();

      // Convert native file path to a fetch-able blob
      const fileUrl = result.filePath.startsWith("file://") ? result.filePath : `file://${result.filePath}`;
      const response = await fetch(Capacitor.convertFileSrc(fileUrl));
      const blob = await response.blob();

      setStatus("idle");
      setHasOrphans(false);
      return blob;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stop failed");
      setStatus("error");
      return null;
    }
  }, [stopTimer]);

  const discard = useCallback(async () => {
    try {
      await StoryForgeRecorder.discardRecording();
      stopTimer();
      setStatus("idle");
      setElapsedSeconds(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Discard failed");
    }
  }, [stopTimer]);

  const recoverChunks = useCallback(async (): Promise<Blob | null> => {
    try {
      const result = await StoryForgeRecorder.getOrphanedRecordings();
      if (result.recordings.length === 0) return null;
      const recording = result.recordings[0];
      const fileUrl = recording.filePath.startsWith("file://") ? recording.filePath : `file://${recording.filePath}`;
      const response = await fetch(Capacitor.convertFileSrc(fileUrl));
      return await response.blob();
    } catch {
      return null;
    }
  }, []);

  const clearRecovery = useCallback(async () => {
    try {
      const result = await StoryForgeRecorder.getOrphanedRecordings();
      for (const rec of result.recordings) {
        await StoryForgeRecorder.deleteOrphanedRecording({ sessionId: rec.sessionId });
      }
      setHasOrphans(false);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    return () => stopTimer();
  }, [stopTimer]);

  return {
    status,
    elapsedSeconds,
    error,
    isSupported: true, // Native always supported
    hasOrphanedChunks: hasOrphans,
    mimeType: platform === "android" ? "audio/ogg" : "audio/mp4",
    fileExtension: platform === "android" ? "ogg" : "m4a",
    analyserNode: null as AnalyserNode | null, // No Web Audio on native; amplitude via getStatus polling
    start,
    pause,
    resume,
    stop,
    discard,
    recoverChunks,
    clearRecovery,
  };
}
